import { Table, Thead, Tbody, Tr, Th, useToast, Flex } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React from 'react';

import { TellerSettingsView } from '../../../../../../types/teller.d';
import { useRefreshTellerInstitutionMutation } from '../requests/teller.gql.generated';
import { AccountSelectionHeader } from './AccountSelectionHeader';
import { AccountSelectionRow } from './AccountSelectionRow';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import {
  GetBankAccountsByCreditFacilityIdDocumentGQLType,
  GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
} from '~/packages/bankAccounts/requests/bankAccounts.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import {
  errorToastConfig,
  errorToastId,
} from '~/packages/common-frontend/constants';

export const Institution = ({
  institutionWithAccounts,
  disconnectAccount,
  disconnectAccountIsLoading,
  cashBalancePreference,
}: TellerSettingsView.InstitutionProps) => {
  const toast = useToast();

  const [
    refreshAccounts,
    { loading: refreshAccountsIsLoading },
  ] = useRefreshTellerInstitutionMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    refetchQueries: [
      GetBankAccountsByCreditFacilityIdDocumentGQLType,
      GetInstitutionAndConnectionDetailsForBorrowerIdDocumentGQLType,
    ],
  });

  const { bankAccounts } = institutionWithAccounts;

  return (
    <Sentry.ErrorBoundary
      onError={() => {
        if (!toast.isActive(errorToastId)) {
          errorToastConfig.title = 'Error selecting institution accounts';
          toast(errorToastConfig);
        }
      }}
    >
      <Flex direction="column" mb="15px" gap="1rem">
        <AccountSelectionHeader
          institutionWithAccounts={institutionWithAccounts}
          disconnectAccount={disconnectAccount}
          disconnectAccountIsLoading={disconnectAccountIsLoading}
          refreshAccounts={refreshAccounts}
          refreshAccountsIsLoading={refreshAccountsIsLoading}
        />

        <Flex width="100%" overflowY="auto">
          <Table fontSize="sm" variant="simple">
            <Thead
              background={colors.tableHeader}
              borderBottom="1px solid lightgrey"
            >
              <Tr>
                <Th minWidth="15rem">Name</Th>
                <Th minWidth="9rem">Type</Th>
                <Th minWidth="9rem">{`${cashBalancePreference}`}</Th>
                <Th minWidth="6rem">Currency</Th>
                <Th minWidth="17rem">Last Refresh</Th>
              </Tr>
            </Thead>
            <Tbody>
              {bankAccounts.map((bankAccount) => {
                return (
                  <AccountSelectionRow
                    cashBalancePreference={cashBalancePreference}
                    key={bankAccount.dataProviderAccountId}
                    institutionAccount={bankAccount}
                    institutionWithAccounts={institutionWithAccounts}
                    isLoading={refreshAccountsIsLoading}
                  />
                );
              })}
            </Tbody>
          </Table>
        </Flex>
      </Flex>
    </Sentry.ErrorBoundary>
  );
};
